import { Injectable, EventEmitter, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { IProduct } from "../shared/models/product.interface";
import { from, map, Observable } from "rxjs";
import { IInventory } from "../shared/models/inventory.interface";
import { IApiResult } from "src/app/shared/models/apiresult.interface";
import { DataService } from "../data.service";
import { db } from "src/app/db.service";
import "lodash";
import { MatDialog } from "@angular/material/dialog";
import { NewProductDialogComponent } from "../dialogs/product-dialog/product-dialog.component";
import { ToastrService } from "ngx-toastr";
import { liveQuery } from "dexie";
declare var _: any;

@Injectable({
  providedIn: "root",
})
export class ProductsService {
  _dataService = inject(DataService);
  dialog = inject(MatDialog);
  toastr = inject(ToastrService);

  // baseUrl: string = "https://localhost:44326/api/";

  baseUrl: string = "https://localhost:5001/api/";

  categorySelected = new EventEmitter<any>();
  categorySelectedOnAdd = new EventEmitter<any>();
  productEdited = new EventEmitter<any>(); // ON PRODUCT, INVENTORY ADD DELETE
  selectedProductId: null;

  constructor(private http: HttpClient) {}

  async getProductionCards(startDate?: any, endDate?: any) {
    let production, sells;

    // 1. Query all data up to the endDate, or all data if no endDate is provided.
    if (endDate) {
      production = await db.production
        .where("date")
        .belowOrEqual(endDate)
        .toArray();
      sells = await db.sells.where("date").belowOrEqual(endDate).toArray();
    } else {
      production = await db.production.toArray();
      sells = await db.sells.toArray();
    }

    const productTotals: { [key: string]: any } = {};

    // Helper function to initialize a product in the totals object if it doesn't exist.
    const ensureProductExists = (productId: string) => {
      if (!productTotals[productId]) {
        const productInfo = _.find(this._dataService.products(), {
          id: productId,
        });
        productTotals[productId] = {
          productId,
          name: productInfo?.name,
          unit: productInfo?.unit,
          unitPrice: productInfo?.sellPrice,
          // Values for the specified date range
          totalLength: 0,
          totalWeight: 0,
          sells: 0,
          // Cumulative values (unfiltered)
          totalProduction: 0,
          totalSells: 0,
        };
      }
    };

    // 2. Process all production records.
    production.forEach((stock) => {
      const { productId, quantity, length, weight, width, date } = stock;
      ensureProductExists(productId);

      // Add to the cumulative total production.
      productTotals[productId].totalProduction += width
        ? quantity * length * width
        : quantity * length;
      productTotals[productId].totalWeight += quantity * length * weight;

      // If the record is within the date range, calculate range-specific values.
      if (!startDate || date >= startDate) {
        productTotals[productId].totalLength += width
          ? quantity * length * width
          : quantity * length;
        productTotals[productId].totalWeight += quantity * length * weight;
      }
    });

    // 3. Process all sell records.
    sells.forEach((sell) => {
      const { productId, quantity, date } = sell;
      if (productId) {
        ensureProductExists(productId);

        // Add to the cumulative total sells.
        productTotals[productId].totalSells += quantity;

        // If the record is within the date range, add to the range-specific sells.
        if (!startDate || date >= startDate) {
          productTotals[productId].sells += quantity;
        }
      }
    });

    // 4. Calculate final values and format the result.
    let result = Object.values(productTotals).map((product) => ({
      ...product,
      remainingQuantity: product.totalProduction - product.totalSells,
    }));

    result.sort(
      (a, b) =>
        b.totalLength - a.totalLength ||
        b.remainingQuantity - a.remainingQuantity
    );

    this._dataService.setLS("productionCards", result);
    return result;
  }

  public getProductionTotalsByDateRange(
    startDate: Date = this._dataService.startDate.value.format("YYYY-MM-DD"),
    endDate: Date = this._dataService.endDate.value.format("YYYY-MM-DD")
  ): Observable<any> {
    return from(
      liveQuery(async () => {
        const productions = await db.production
          .where("date")
          .between(startDate, endDate, true, true)
          .toArray();

        const products = this._dataService.products();
        const productMap = new Map(products.map((p) => [p.id, p]));

        const groupedResults: any = {};

        for (const item of productions) {
          if (
            typeof item.quantity !== "number" ||
            typeof item.length !== "number"
          ) {
            continue;
          }

          const productInfo: any = productMap.get(item.productId);

          if (!productInfo) {
            console.warn(`Product with ID ${item.productId} not found.`);
            continue;
          }

          const hasWidth = typeof item.width === "number" && item.width > 0;
          const value = hasWidth
            ? item.quantity * item.length * item.width
            : item.quantity * item.length;

          if (!groupedResults[item.productId]) {
            groupedResults[item.productId] = {
              name: productInfo.name || `Unknown ID: ${item.productId}`,
              sum: 0,
              unit: productInfo.unit || "unknown", // Products with no unit are still grouped as "unknown"
            };
          }

          groupedResults[item.productId].sum += value;
        }

        return groupedResults;
      })
    ).pipe(
      map((groupedData) => {
        const finalProducts: any[] = Object.values(groupedData);
        const finalSum: { [key: string]: number } = {};

        for (const product of finalProducts) {
          const unit = product.unit;

          // --- MODIFICATION HERE ---
          // Only process this product for the sum object if its unit is known.
          if (unit && unit !== "unknown") {
            if (!finalSum[unit]) {
              finalSum[unit] = 0;
            }
            finalSum[unit] += product.sum;
          }
        }

        return {
          products: finalProducts, // The products array still contains items with "unknown" units
          sum: finalSum, // The sum object will NOT have an "unknown" key
        };
      })
    );
  }

  ProductDetailsDialog(productId?) {
    let dialogRef,
      data = null;
    if (productId) {
      data = this._dataService.products().find((x) => x.id == productId);
    }
    dialogRef = this.dialog.open(NewProductDialogComponent, {
      width: "950px",
      maxHeight: "700px",
      data: data,
      panelClass: "materialDialog",
      closeOnNavigation: true,
      disableClose: this._dataService.mobileDevice
        ? false
        : productId
        ? false
        : true,
    });
    dialogRef.afterClosed().subscribe(
      (res) => {
        if (res) {
          this.selectedProductId = res.id;
        }
      },
      () => {
        this.toastr.error("Xəta baş verdi. Zəhmət olmasa yenidən cəhd edin");
      }
    );
  }

  async getProductOperations(productId) {
    let stock = await db.production
      .where("productId")
      .equals(productId)
      .toArray();
    let sells = await db.sells.where("productId").equals(productId).toArray();

    return stock.concat(sells);
  }

  addIstehsal(data: object) {
    return this.http.post(
      "https://tekoplast.az/hesabat/api.php/records/production",
      data
    );
  }

  GetProducts(): Observable<IProduct[]> {
    return this.http.get<IProduct[]>(this.baseUrl);
  }

  getProduction(): Observable<IApiResult> {
    return this.http.get<IApiResult>(
      "https://tekoplast.az/hesabat/api.php/records/production?join=personnel&join=products&order=date,desc&order=id,desc"
    );
  }

  // GET PRODUCTS OF A CATEGORY
  getProduct(productId: number) {
    return this.http.get<object>(this.baseUrl + "products/" + productId);
  }

  // GET PRODUCTS OF A CATEGORY BY STOCK ID
  getProductByStock(productId: number, stockId: number) {
    return this.http.get<object>(
      this.baseUrl + "products/" + productId + "/" + stockId
    );
  }

  // INVENTORY DETAILS BY DATE
  getInventoryDetailsByDate(categoryId: number, date1: Date, date2: Date) {
    return this.http.get<object>(
      this.baseUrl + `products/date/${categoryId}/${date1}/${date2}`
    );
  }

  // GET PRODUCTS BY DATE AND STOCK
  getProductsByDate(date1: Date, date2: Date, stockId?: number) {
    return this.http.get<object>(
      this.baseUrl + `productcategories/date/${date1}/${date2}/${stockId}`
    );
  }

  // GET PRODUCT DETAILS BY PRODUCT NAME
  getProductDetailsByName(CatId: Number, ProductName: string) {
    return this.http.get<object>(
      this.baseUrl + "getproductdetailsbyname/" + CatId + "/" + ProductName
    );
  }

  // PRODUCT CATEGORIES GET
  getProductCategories() {
    return this.http.get<object>(this.baseUrl + "productcategories");
  }

  // GET PRODUCT CATEGORIES BY STOCK ID
  getProductCategoriesByStock(stockId: number) {
    return this.http.get<object>(
      this.baseUrl + `productcategories/stock/${stockId}/1`
    );
  }

  addProductCategory(productCategory: object) {
    return this.http.post(this.baseUrl + "productcategories/", productCategory);
  }

  deleteProductCategory(Id: number) {
    return this.http.delete(this.baseUrl + "productcategories/" + Id);
  }

  // ADD INVENTORY
  addInventory(inventory: object) {
    return this.http.post(this.baseUrl + "inventories/", inventory);
  }

  getInventoryById(id: number) {
    return this.http.get<IInventory>(this.baseUrl + "inventories/" + id);
  }

  addProduct(product: IProduct) {
    return this.http.post(this.baseUrl + "products1", product);
  }

  editProduct(productId: number, product: IProduct) {
    return this.http.put(this.baseUrl + "products/" + productId, product);
  }

  deleteProduct(productId: number) {
    return this.http.delete(this.baseUrl + "products/" + productId);
  }

  searchProduct(text: string) {
    return this.http.get<IProduct[]>(this.baseUrl + "search/" + text);
  }

  // GET STOCKS LIST
  getStocks() {
    return this.http.get<object>(this.baseUrl + "stocks");
  }

  getStockByID(id: number) {
    return this.http.get<object>(this.baseUrl + "stocks" + id);
  }

  // GET UNITS
  getUnits() {
    return this.http.get<Object>(this.baseUrl + "units");
  }
}
